// Password Validation Class
// --------------------------
//
//

import { EMPTY_STRING } from "../constants/definitions";

const MIN_REQUIRED_PASSWORD_LENGTH = 12;

const PASSWORDS_DO_NOT_MATCH_ERROR = "Passphrases do not match";
const PASSWORDS_LENGTH_TOO_SHORT_ERROR =
  "Passphrase is too short, refer to your Security Policy for requirements";
const PASSWORD_NO_LOWERCASE =
  "Passphrase does not contain a lower case character";
const PASSWORD_NO_UPPERCASE =
  "Passphrase does not contain an upper case character";
const PASSWORD_NO_NUMERIC = "Passphrase does not contain a numeric character";
const PASSWORD_NO_SPECIALCHAR =
  "Passphrase does not contain a symbol character, valid symbols are ^$*.[]{}()?-\"!@#%&/,><':;|_~`";

const LIST_OF_ERRORS = [
  PASSWORDS_DO_NOT_MATCH_ERROR,
  PASSWORDS_LENGTH_TOO_SHORT_ERROR,
  PASSWORD_NO_LOWERCASE,
  PASSWORD_NO_UPPERCASE,
  PASSWORD_NO_NUMERIC,
  PASSWORD_NO_SPECIALCHAR,
];

//const UPPER_CASE_CHARSET = /([A-Z]+)/g
//const LOWER_CASE_CHARSET = /([a-z]+)/g
//const NUMERIC_CASE_CHARSET = /([0-9]+)/g
const SPECIAL_CASE_CHARSET = /[ !@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/;

// User-facing password requirements, used to render a live checklist so people
// aren't guessing what's required. These MUST mirror both validateFirstPassword
// below AND the Cognito user-pool password policy (MinimumLength 12, require
// lower/upper/number/symbol). Keep all three in sync if the pool policy changes.
export interface PasswordRequirement {
  label: string;
  isMet: (candidatePassword: string) => boolean;
}

export const PASSWORD_REQUIREMENTS: PasswordRequirement[] = [
  { label: "At least 12 characters", isMet: (candidatePassword) => candidatePassword.length >= MIN_REQUIRED_PASSWORD_LENGTH },
  { label: "A lowercase letter (a–z)", isMet: (candidatePassword) => /[a-z]/.test(candidatePassword) },
  { label: "An uppercase letter (A–Z)", isMet: (candidatePassword) => /[A-Z]/.test(candidatePassword) },
  { label: "A number (0–9)", isMet: (candidatePassword) => /\d/.test(candidatePassword) },
  { label: "A symbol (e.g. ! @ # $ %)", isMet: (candidatePassword) => SPECIAL_CASE_CHARSET.test(candidatePassword) },
];

class PasswordValidationClass {
  // Validate First Password
  // ------------------------
  // If the current error is blank such that no error previously set will be over written
  // Or if the current error is set by us such that we can change it
  // Then check again
  //
  // This includes overwriting the second password check
  //
  validateFirstPassword(
    password: string,
    currentError: string,
    evaluateEmptyPassword: boolean
  ): string {
    if (
      EMPTY_STRING === currentError ||
      LIST_OF_ERRORS.indexOf(currentError) > -1
    ) {
      // If the password is empty and we have not been asked to evaluate empty then return no error
      if (EMPTY_STRING === password && !evaluateEmptyPassword) {
        return EMPTY_STRING;
      }

      if (!password) {
        return PASSWORDS_LENGTH_TOO_SHORT_ERROR;
      } else if (password.length < MIN_REQUIRED_PASSWORD_LENGTH) {
        return PASSWORDS_LENGTH_TOO_SHORT_ERROR;
      } else if (!/[a-z]/.test(password)) {
        return PASSWORD_NO_LOWERCASE;
      } else if (!/[A-Z]/.test(password)) {
        return PASSWORD_NO_UPPERCASE;
      } else if (!/\d/.test(password)) {
        return PASSWORD_NO_NUMERIC;
      } else if (!SPECIAL_CASE_CHARSET.test(password)) {
        return PASSWORD_NO_SPECIALCHAR;
      } else {
        // No error found - return an empty string
        return EMPTY_STRING;
      }
    }

    // If we are not fit to evaluate the error then return the current error so it doesnt change state
    return currentError;
  }

  // Validate Second Password
  // -------------------------
  // If the current error is blank such that no error previously set will be over written
  // Or if the current error is set by us such that we can change it
  // Then check again
  //
  // This does NOT include overwriting the first password check
  //
  validateSecondPassword(
    firstPassword: string,
    secondPassword: string,
    currentError: string
  ): string {
    if (
      EMPTY_STRING === currentError ||
      PASSWORDS_DO_NOT_MATCH_ERROR === currentError
    ) {
      if (EMPTY_STRING === firstPassword) {
        // If the first password is blank then do no evaluate the second password
        return EMPTY_STRING;
      } else if (firstPassword !== secondPassword) {
        // If the passwords dont match return error
        return PASSWORDS_DO_NOT_MATCH_ERROR;
      } else {
        // No error found - return an empty string
        return EMPTY_STRING;
      }
    }

    // If we are not fit to evaluate the error then return the current error so it doesnt change state
    return currentError;
  }
}

export default PasswordValidationClass;
